"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import {
  detectInAppBrowser,
  detectInstallPlatform,
  isStandaloneDisplay,
  type InAppBrowserInfo,
  type InstallPlatform,
} from "../../../lib/pwa/platform";
import { resolveInstallAppName } from "../../../lib/pwa/autoCardRoutes";
import { InstallGuideSheet } from "./InstallGuideSheet";

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export const INSTALL_DISMISSED_KEY = "agiliza-pwa-install-dismissed-at";
export const INSTALL_DISMISS_DAYS = 14;

// SPEC_DEVIATION: o design lista platform, inApp, isInstalled,
// canPromptNatively, hasInstallPath, brandName, setBrandName, requestInstall e
// openGuide. Somam-se appName, isGuideOpen, closeGuide, autoCardDismissed e
// dismissAutoCard.
// Reason: o adiamento de 14 dias precisa valer na sessão mesmo sem
// localStorage (modo privado), então o estado vive aqui e não só no storage;
// a folha é aberta e fechada pelo provedor; e o nome do app (GUIA-02) é o mesmo
// para cartão, folha e item fixo.
export type PwaInstallContextValue = {
  platform: InstallPlatform;
  inApp: InAppBrowserInfo;
  isInstalled: boolean;
  canPromptNatively: boolean;
  /** Prompt nativo, ou iOS/iPadOS, ou Android. */
  hasInstallPath: boolean;
  brandName: string | null;
  setBrandName(name: string | null): void;
  /** Nome do app para o convite (GUIA-02). */
  appName: string;
  /** Prompt nativo, se existir; senão abre a folha de passos. */
  requestInstall(): Promise<void>;
  openGuide(): void;
  closeGuide(): void;
  isGuideOpen: boolean;
  /** Adiamento do cartão automático (14 dias). Não vale para bloco nem item fixo. */
  autoCardDismissed: boolean;
  dismissAutoCard(): void;
};

const noop = () => undefined;

const INERT_VALUE: PwaInstallContextValue = {
  platform: "desktop",
  inApp: { inApp: false, app: null },
  isInstalled: false,
  canPromptNatively: false,
  hasInstallPath: false,
  brandName: null,
  setBrandName: noop,
  appName: "Agiliza",
  requestInstall: async () => undefined,
  openGuide: noop,
  closeGuide: noop,
  isGuideOpen: false,
  autoCardDismissed: true,
  dismissAutoCard: noop,
};

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null);

/*
 * O localStorage pode lançar (modo privado, cota, política do navegador). O
 * convite continua funcionando, só sem lembrar o adiamento entre visitas.
 */
function readAutoCardDismissed(): boolean {
  try {
    const dismissedAt = Number(window.localStorage.getItem(INSTALL_DISMISSED_KEY) || 0);
    if (!dismissedAt) return false;
    return Date.now() < dismissedAt + INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function writeAutoCardDismissed() {
  try {
    window.localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
  } catch {
    // Sem storage: o adiamento vale só nesta sessão (estado do provedor).
  }
}

function clearAutoCardDismissed() {
  try {
    window.localStorage.removeItem(INSTALL_DISMISSED_KEY);
  } catch {
    // Sem storage: nada a limpar.
  }
}

const isServiceWorkerSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  (window.location.protocol === "https:" || window.location.hostname === "localhost");

export function PwaInstallProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  // Estado inicial inerte (igual no servidor e no cliente): a detecção roda no
  // efeito, para não haver divergência de hidratação nem convite piscando no
  // app instalado.
  const [detected, setDetected] = useState(false);
  const [platform, setPlatform] = useState<InstallPlatform>("desktop");
  const [inApp, setInApp] = useState<InAppBrowserInfo>(INERT_VALUE.inApp);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [brandName, setBrandName] = useState<string | null>(null);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [autoCardDismissed, setAutoCardDismissed] = useState(true);
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const { userAgent, platform: navigatorPlatform, maxTouchPoints } = window.navigator;
    setPlatform(
      detectInstallPlatform({ userAgent, platform: navigatorPlatform, maxTouchPoints })
    );
    setInApp(detectInAppBrowser(userAgent));
    setIsInstalled(isStandaloneDisplay());
    setAutoCardDismissed(readAutoCardDismissed());
    setDetected(true);
  }, []);

  useEffect(() => {
    if (!isServiceWorkerSupported()) return;

    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch((error) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[PWA] Service worker registration failed", error);
      }
    });
  }, []);

  useEffect(() => {
    // Ouvido sempre, mesmo com o adiamento do cartão gravado: o bloco da
    // confirmação e o item fixo usam o mesmo prompt (MENU-04).
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredPromptRef.current = event as BeforeInstallPromptEvent;
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const handleInstalled = () => {
      deferredPromptRef.current = null;
      setDeferredPrompt(null);
      setIsInstalled(true);
      setIsGuideOpen(false);
      clearAutoCardDismissed();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const openGuide = useCallback(() => setIsGuideOpen(true), []);
  const closeGuide = useCallback(() => setIsGuideOpen(false), []);

  const dismissAutoCard = useCallback(() => {
    writeAutoCardDismissed();
    setAutoCardDismissed(true);
  }, []);

  const requestInstall = useCallback(async () => {
    const promptEvent = deferredPromptRef.current;
    if (!promptEvent) {
      setIsGuideOpen(true);
      return;
    }

    // O evento só pode ser usado uma vez.
    deferredPromptRef.current = null;
    setDeferredPrompt(null);

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;

      if (choice.outcome === "accepted") {
        clearAutoCardDismissed();
        // O Chrome instala logo depois do aceite; o convite some já, sem
        // esperar o `appinstalled`.
        setIsInstalled(true);
      } else {
        // Recusa por qualquer convite adia o cartão automático (borda da spec).
        writeAutoCardDismissed();
        setAutoCardDismissed(true);
      }
    } catch {
      // Prompt indisponível: o passo a passo continua sendo um caminho.
      setIsGuideOpen(true);
    }
  }, []);

  const canPromptNatively = deferredPrompt !== null;
  const hasInstallPath = canPromptNatively || (detected && platform !== "desktop");
  const appName = resolveInstallAppName({ pathname, brandName });

  const value = useMemo<PwaInstallContextValue>(
    () => ({
      platform,
      inApp,
      isInstalled,
      canPromptNatively,
      hasInstallPath,
      brandName,
      setBrandName,
      appName,
      requestInstall,
      openGuide,
      closeGuide,
      isGuideOpen,
      autoCardDismissed,
      dismissAutoCard,
    }),
    [
      platform,
      inApp,
      isInstalled,
      canPromptNatively,
      hasInstallPath,
      brandName,
      appName,
      requestInstall,
      openGuide,
      closeGuide,
      isGuideOpen,
      autoCardDismissed,
      dismissAutoCard,
    ]
  );

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
      <InstallGuideSheet
        open={isGuideOpen}
        onClose={closeGuide}
        appName={appName}
        platform={platform}
        inApp={inApp.inApp}
      />
    </PwaInstallContext.Provider>
  );
}

/** Contexto de instalação; fora do provedor devolve um valor inerte. */
export function usePwaInstall(): PwaInstallContextValue {
  return useContext(PwaInstallContext) ?? INERT_VALUE;
}
